<template>
  <div
    class="rf-bulk-action-menu border-beige-10 bg-white-1 flex items-center gap-0.5 overflow-hidden rounded-t-lg border border-b-0 border-solid pl-4"
    data-cy="table-select"
  >
    <RfCheckbox
      data-cy="table-select--all"
      class="inline-flex items-center justify-center"
      :data-cy-value="selected.size"
      :modelValue="!!(selected.size && isAllSelected)"
      :intermediate="!!(selected.size && !isAllSelected)"
      @update:modelValue="emit(isAllSelected ? 'deselectAll' : 'selectAll')"
    />
    <RfMenu
      ref="tableSelect"
      class="[&_.rf-menu--button]:hover:bg-beige-1 [&_.rf-menu--opened]:bg-beige-1 h-full [&_.rf-menu--button]:h-full [&_.rf-menu--button]:rounded-l-none! [&_.rf-menu--button]:rounded-r-sm [&_.rf-menu--button]:px-1.5"
    >
      <template #menu>
        <div class="list flex w-36 flex-col gap-4 p-4">
          <button
            class="rounded p-2 text-left"
            data-cy="table-select--dropdown-all"
            :class="{ 'bg-beige-1! text-beige-90!': isAllSelected }"
            @click="() => (emit('selectAll'), $refs.tableSelect.hide())"
          >
            All
          </button>
          <button
            data-cy="table-select--dropdown-enabled"
            class="rounded p-2 text-left"
            :class="{
              'bg-beige-1! text-beige-90!':
                !!selectedStatuses[StatusesSimplifiedConstants.running.type] &&
                !selectedStatuses[StatusesSimplifiedConstants.paused.type] &&
                !selectedStatuses[StatusesSimplifiedConstants.ended.type] &&
                !selectedStatuses[StatusesSimplifiedConstants.limited.type],
            }"
            @click="
              () => (
                emit('selectByPreset', StatusesSimplifiedConstants.running.type),
                $refs.tableSelect.hide()
              )
            "
          >
            {{ StatusesSimplifiedConstants.running.title }}
          </button>

          <button
            class="rounded p-2 text-left"
            data-cy="table-select--dropdown-paused"
            :class="{
              'bg-beige-1! text-beige-90!':
                !selectedStatuses[StatusesSimplifiedConstants.running.type] &&
                !!selectedStatuses[StatusesSimplifiedConstants.paused.type] &&
                !selectedStatuses[StatusesSimplifiedConstants.ended.type] &&
                !selectedStatuses[StatusesSimplifiedConstants.limited.type],
            }"
            @click="
              () => (
                emit('selectByPreset', StatusesSimplifiedConstants.paused.type),
                $refs.tableSelect.hide()
              )
            "
          >
            {{ StatusesSimplifiedConstants.paused.title }}
          </button>

          <button
            class="rounded p-2 text-left"
            data-cy="table-select--dropdown-disabled"
            :class="{
              'bg-beige-1! text-beige-90!':
                !selectedStatuses[StatusesSimplifiedConstants.running.type] &&
                !selectedStatuses[StatusesSimplifiedConstants.paused.type] &&
                !!selectedStatuses[StatusesSimplifiedConstants.ended.type] &&
                !selectedStatuses[StatusesSimplifiedConstants.limited.type],
            }"
            @click="
              () => (
                emit('selectByPreset', StatusesSimplifiedConstants.ended.type),
                $refs.tableSelect.hide()
              )
            "
          >
            {{ StatusesSimplifiedConstants.ended.title }}
          </button>

          <button
            class="rounded p-2 text-left"
            data-cy="table-select--dropdown-limited"
            :class="{
              'bg-beige-1! text-beige-90!':
                !selectedStatuses[StatusesSimplifiedConstants.running.type] &&
                !selectedStatuses[StatusesSimplifiedConstants.paused.type] &&
                !selectedStatuses[StatusesSimplifiedConstants.ended.type] &&
                !!selectedStatuses[StatusesSimplifiedConstants.limited.type],
            }"
            @click="
              () => (
                emit('selectByPreset', StatusesSimplifiedConstants.limited.type),
                $refs.tableSelect.hide()
              )
            "
          >
            {{ StatusesSimplifiedConstants.limited.title }}
          </button>
        </div>
      </template>
    </RfMenu>

    <Transition>
      <span
        v-if="selected.size"
        data-cy="table-select--actions"
        class="text-body text-grey-1 inline-flex items-center gap-2"
      >
        <RfColorTag
          v-if="selected.size"
          class="rf-tags-group--tag"
          :title="`${selected.size} selected`"
          pill
          data-cy="table-select--selected"
          :data-cy-value="selected.size"
          backgroundColor="#FFF"
          @clear="emit('deselectAll')"
        />

        <RfButtonNew v-if="selected.size === 1" type="table" @click="emit('edit')">
          <RfEditIcon class="h-6! w-6!" />
          Edit
        </RfButtonNew>
        <RfButtonNew
          v-if="
            !selectedStatuses[StatusesSimplifiedConstants.running.type] &&
            (!!selectedStatuses[StatusesSimplifiedConstants.paused.type]
              ? !selectedStatuses[StatusesSimplifiedConstants.ended.type]
              : !!selectedStatuses[StatusesSimplifiedConstants.ended.type]) &&
            !selectedStatuses.disableStatusChange
          "
          type="table"
          size="large"
          @click="emit('status', 'running')"
        >
          <RfPlayIcon class="fill-beige-50 h-6! w-6!" />
          {{
            !!selectedStatuses[StatusesSimplifiedConstants.paused.type] &&
            !selectedStatuses[StatusesSimplifiedConstants.ended.type]
              ? "Resume"
              : "Start"
          }}
        </RfButtonNew>

        <RfButtonNew
          v-if="
            !!selectedStatuses[StatusesSimplifiedConstants.running.type] &&
            !selectedStatuses[StatusesSimplifiedConstants.paused.type] &&
            !selectedStatuses[StatusesSimplifiedConstants.ended.type] &&
            !selectedStatuses.disableStatusChange
          "
          type="table"
          size="large"
          @click="emit('status', 'paused')"
        >
          <RfPauseIcon class="fill-beige-50 h-6! w-6!" />
          Pause
        </RfButtonNew>

        <RfButtonNew
          v-if="
            !!selectedStatuses[StatusesSimplifiedConstants.running.type] &&
            !selectedStatuses[StatusesSimplifiedConstants.paused.type] &&
            !selectedStatuses[StatusesSimplifiedConstants.ended.type] &&
            !selectedStatuses.disableStatusChange
          "
          type="table"
          size="large"
          @click="emit('status', 'ended')"
        >
          <RfStopIcon class="fill-beige-50 h-6! w-6!" />
          Stop
        </RfButtonNew>

        <RfButtonNew v-if="selected.size === 1" type="table" size="large" @click="emit('clone')">
          <RfCloneIcon class="fill-beige-50 h-6! w-6!" />
          Clone
        </RfButtonNew>

        <RfButtonNew type="table" size="large" @click="emit('export')">
          <RfDownloadIcon class="stroke-beige-50 h-6! w-6!" />
          Export to CSV
        </RfButtonNew>

        <RfButtonNew
          v-if="userStore.isCurrentAdmin || userStore.isCurrentSuperAdmin"
          type="table"
          size="large"
          @click="emit('bulkEdit')"
        >
          <RfEditIcon class="h-6! w-6!" />
          Edit
        </RfButtonNew>

        <RfButtonNew type="table" size="large" @click="emit('delete')">
          <RfDeleteIcon class="fill-beige-50 h-6! w-6!" />
          Delete
        </RfButtonNew>
      </span>
    </Transition>
  </div>
</template>

<script setup>
import RfMenu from "@/components/menus/RfMenu.vue";
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import {
  SimplifyStatusesMapConstants,
  StatusesSimplifiedConstants,
} from "@/utils/constants/PromptStatusesConstants";
import RfDeleteIcon from "@/components/icons/RfDeleteIcon.vue";
import RfCloneIcon from "@/components/icons/RfCloneIcon.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfEditIcon from "@/components/icons/RfEditIcon.vue";
import RfPauseIcon from "@/components/icons/RfPauseIcon.vue";
import RfStopIcon from "@/components/icons/RfStopIcon.vue";
import getPromptStatus from "@/utils/prompts/getPromptStatus";
import RfPlayIcon from "@/components/icons/RfPlayIcon.vue";
import RfDownloadIcon from "@/components/icons/RfDownloadIcon.vue";
import { computed } from "vue";
import { isLatestExperimentActive, isPushNotification } from "@/utils/prompts/promptHelpers";
import { useUserStore } from "@/pinia/userStore";

const emit = defineEmits([
  "selectAll",
  "deselectAll",
  "selectByPreset",
  "edit",
  "status",
  "clone",
  "export",
  "bulkEdit",
  "delete",
]);
const props = defineProps({
  selected: { type: Map, default: null },
  tableData: { type: Object, default: () => ({ page: 1, perPage: 10, totalCount: 10 }) },
  timezone: { type: String, default: "" },
});

const userStore = useUserStore();

const isAllSelected = computed(() => props.tableData.showing === props.selected.size);

const selectedStatuses = computed(() =>
  [...props.selected].reduce((acc, [_, prompt]) => {
    const status = SimplifyStatusesMapConstants[getPromptStatus(prompt, props.timezone).type].type;
    return {
      ...acc,
      [status]: +!!acc[status] + 1,
      disableStatusChange:
        acc.disableStatusChange ||
        !!prompt.sequence_id ||
        isLatestExperimentActive({ path: prompt }) ||
        status === SimplifyStatusesMapConstants.limited.type ||
        (isPushNotification(prompt) && status === SimplifyStatusesMapConstants.ended.type),
    };
  }, {}),
);
</script>
